import React, { Fragment, useState, useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { ButtonModalClose } from "./ButtonModalClose";
import { useAuth } from "../context/AuthContext";
import AxisSpinner from "./AxisSpinner";
import "chart.js/auto";
import { Pie } from "react-chartjs-2";
import { AxisChartColours } from "../util/AxisChartColours";
import { AxisLineSeparator } from "../components/AxisLineSeparator";
import { ShiftTableTitle } from "./ShiftTableTitle";
import ProgressBar from "react-bootstrap/ProgressBar";
import { saveAs } from "file-saver";

export const TimesheetWeekDownload = ({ timesheetWeek }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownLoadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [tree, setTree] = useState(null);

  useEffect(() => {
    if (timesheetWeek === null || timesheetWeek === undefined) return;

    fetchTree();
  }, [timesheetWeek]);

  const fetchTree = async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          timesheet_week_id: timesheetWeek?.id,
        },
      };

      const result = await authAxios().post(
        "/api/backoffice/reporting/timesheet_week_tree",
        null,
        config,
      );

      setTree(result.data);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log("error:");
      console.log(err);
    }
  };

  const downloadFile = async (file) => {
    // Reset download progress and set current file being downloaded
    setDownLoadProgress(0);
    setDownloading(true);
    setCurrentFile(file);

    try {
      const config = {
        responseType: "arraybuffer",
        params: {
          timesheet_week_id: timesheetWeek?.id,
          path: file?.path,
        },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent?.progress !== undefined) {
            const progress = progressEvent?.progress * 100;
            const rounded = Math.round(progress * 100) / 100;
            setDownLoadProgress(rounded);
          }
        },
      };

      const response = await authAxios().post(
        "/api/backoffice/reporting/timesheet_week_tree_file",
        null,
        config,
      );

      // Determine file type based on extension
      var fileType = "application/pdf";
      if (file?.name?.endsWith(".csv")) {
        fileType = "text/csv";
      } else if (file?.name?.endsWith(".zip")) {
        fileType = "application/zip";
      } else if (file?.name?.endsWith(".xlsx")) {
        fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }

      // Set progress to 100% when done
      setDownLoadProgress(100);

      // Create blob and download
      var blob = new Blob([response.data], { type: fileType });
      var url = window.URL.createObjectURL(blob);
      saveAs(url, file?.name);

      // Clear download state after a small delay to show 100% completion
      setTimeout(() => {
        setDownloading(false);
        setCurrentFile(null);
      }, 800);
    } catch (err) {
      setDownloading(false);
      setCurrentFile(null);
      console.error("Download error:", err);
    }
  };

  const getFileIcon = (filename) => {
    if (filename.endsWith('.pdf')) {
      return <i className="far fa-file-pdf text-danger me-2"></i>;
    } else if (filename.endsWith('.xlsx')) {
      return <i className="far fa-file-excel text-success me-2"></i>;
    } else if (filename.endsWith('.zip')) {
      return <i className="far fa-file-archive text-warning me-2"></i>;
    } else if (filename.endsWith('.csv')) {
      return <i className="far fa-file-alt text-info me-2"></i>;
    } else {
      return <i className="far fa-file me-2"></i>;
    }
  };

  const getDownloadOption = (option) => {
    const isDownloading = downloading;
    const isCurrentFile = currentFile && currentFile.path === option.path;
    
    return (
      <td
        onClick={() => {
          if (isDownloading) return; // Prevent clicking if any download is in progress
          downloadFile(option);
        }}
        style={{ 
          cursor: isDownloading ? 'not-allowed' : 'pointer', 
          color: isCurrentFile ? '#28a745' : (isDownloading ? '#6c757d' : '#0d6efd'),
          textDecoration: 'underline',
          opacity: isDownloading && !isCurrentFile ? 0.6 : 1
        }}
        className="d-flex align-items-center"
        title={isDownloading && !isCurrentFile ? "Please wait for current download to finish" : `Download ${option.name}`}
      >
        {getFileIcon(option?.name)}
        <span>{option?.name}</span>
        {isCurrentFile ? (
          <i className="fas fa-circle-notch fa-spin ms-2 text-success"></i>
        ) : (
          <i className="fas fa-download ms-2 text-secondary"></i>
        )}
      </td>
    );
  };

  const getDownloadOptions = () => {
    // Group files by extension
    const groupedFiles = {};
    
    if (tree?.children) {
      tree.children.forEach(file => {
        if (file?.children?.length > 0) return;
        
        // Extract extension
        const match = file.name.match(/\.([^.]+)$/);
        const extension = match ? match[1].toLowerCase() : 'other';
        
        if (!groupedFiles[extension]) {
          groupedFiles[extension] = [];
        }
        
        groupedFiles[extension].push(file);
      });
    }
    
    // Get all extensions and sort them in reverse order
    const extensions = Object.keys(groupedFiles).sort().reverse();
    const hasFiles = extensions.length > 0;
    
    return (
      <Fragment>
        {downloading && currentFile && (
          <div className="mb-3">
            <Card className="border-primary mb-2">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <strong className="text-primary">
                    Downloading: {currentFile.name}
                  </strong>
                </div>
                <ProgressBar
                  striped
                  animated
                  variant="primary"
                  now={downloadProgress}
                  label={`${downloadProgress}%`}
                />
              </Card.Body>
            </Card>
          </div>
        )}

        <Card className="shadow-sm">
          <Card.Header className="bg-light">
            <h5 className="mb-0">
              <i className="fas fa-download me-2"></i>
              Available Downloads
            </h5>
          </Card.Header>
          <Card.Body className={hasFiles ? "p-0" : "text-center py-4"}>
            {hasFiles ? (
              <Table hover responsive className="mb-0">
                <tbody>
                  {extensions.map((extension, extIndex) => (
                    <React.Fragment key={extIndex}>
                      <tr className="bg-light">
                        <th className="ps-3 py-2">
                          {extension.toUpperCase()} Files
                        </th>
                      </tr>
                      {(extension === 'pdf' 
                        ? [...groupedFiles[extension]].sort((a, b) => a.name.localeCompare(b.name))
                        : extension === 'xlsx'
                        ? [...groupedFiles[extension]].sort((a, b) => {
                            // Put manifest.xlsx at the top
                            if (a.name === 'manifest.xlsx') return -1;
                            if (b.name === 'manifest.xlsx') return 1;
                            // Otherwise sort alphabetically
                            return a.name.localeCompare(b.name);
                          })
                        : groupedFiles[extension]
                      ).map((file, fileIndex) => (
                        <tr key={`${extIndex}-${fileIndex}`}>
                          {getDownloadOption(file)}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted">
                <i className="far fa-folder-open fa-3x mb-3"></i>
                <p>No files available for download</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Fragment>
    );
  };

  const SpinnerOrData = () => {
    if (loading) return <AxisSpinner loading={loading} />;
    return <Fragment>{getDownloadOptions()}</Fragment>;
  };

  return <Fragment>{SpinnerOrData()}</Fragment>;
};
